import { useAtomValue } from 'jotai';

import { JobApplicationStatusKey } from '@restworld/utility-types';

import { /* useJobApplicationPerformance, */ useJobApplyStatus, useJobPositionDetails } from '../../../hooks';
import { JobApplicationEntryPoint } from '../job-application-entry-point';
import { JobApplicationInterest } from '../../job-applications/components/job-application-interest';
import JobDetailBlock from '../../job-detail-block';
import { jobActiveStatusAtom, jobApplicationAtom, jobPositionApiDataAtom } from '../job-position.atoms';
import { ReopenTriggerButton } from '../../offline-job-landing';

export const JobInfoCard = ({ ambassadorRefCode, testId }: { ambassadorRefCode?: string; testId?: string }) => {
  const jobPositionApiData = useAtomValue(jobPositionApiDataAtom);
  const jobApplication = useAtomValue(jobApplicationAtom);
  const jobActiveStatus = useAtomValue(jobActiveStatusAtom);

  const jobPositionDetails = useJobPositionDetails({ jobPositionApiData });
  const { handleFetchJobPositionApplyStatus } = useJobApplyStatus();

  const jopPositionId = jobPositionApiData?.id;
  const jobApplicationStatusKey = jobApplication?.status?.key as JobApplicationStatusKey;
  const showApplicationConfirmationActions = jopPositionId && jobApplicationStatusKey === 'worker_jp_proposed';
  // Prefer `first_published_at` (Restworld go-live moment) over `published_at`
  // (which for ATS-imported JPs is set by the backend SM but for legacy ATS rows
  // can still carry the source ATS feed's `<publishedDate>` — months stale).
  // See the field doc in `libs/utility-types/src/employers.ts`. Fallback covers
  // historical rows where `first_published_at` is still nil pre-backfill.
  const jobPublishedAt = jobPositionApiData?.first_published_at ?? jobPositionApiData?.published_at;
  const jobWentOfflineAt = jobPositionApiData?.went_offline_at;

  // `jobInterestEl` is rendered by JobDetailBlock as a full-width row
  // immediately below the apply+share row. We reuse that slot for the
  // offline-JP reopen trigger so it lands on a new line under "Candidati"
  // (proposed-application UX and offline-JP UX are mutually exclusive —
  // a worker on a `worker_jp_proposed` flow is by definition on an active JP).
  let jobInterestEl: React.ReactNode = null;
  if (showApplicationConfirmationActions) {
    jobInterestEl = (
      <JobApplicationInterest
        restaurantId={null}
        jobPositionId={jopPositionId}
        variant="jp-page"
        onSuccessfulUpdate={() => {
          handleFetchJobPositionApplyStatus();
        }}
      />
    );
  } else if (!jobActiveStatus) {
    jobInterestEl = <ReopenTriggerButton />;
  }

  return (
    <JobDetailBlock
      showRestaurant={jobPositionDetails?.showRestaurant}
      jobPositionDetails={jobPositionDetails?.jobPositionDetails}
      ambassadorRefCode={ambassadorRefCode}
      jobInterestEl={jobInterestEl}
      // Hide both the apply button AND the share icon when the JP is
      // offline: the offer no longer accepts candidatures, and sharing
      // a dead link is noise. The reopen trigger renders below via
      // `jobInterestEl` and is the only CTA we want in that frame.
      hideJobApplicationEntryPoint={showApplicationConfirmationActions || !jobActiveStatus}
      showShareButton={jobActiveStatus}
      publishedAt={jobPublishedAt}
      wentOfflineAt={!jobActiveStatus ? jobWentOfflineAt : undefined}
    >
      <JobApplicationEntryPoint testId={testId} />
    </JobDetailBlock>
  );
};
